/**
 * Serviço de integração com Mercado Pago
 * Suporta pagamentos via PIX e Cartão de Crédito
 */

const MP_PUBLIC_KEY = process.env.REACT_APP_MP_PUBLIC_KEY || ''
const API_URL = process.env.REACT_APP_API_URL || 'https://us-central1-tetriz-sandevcode.cloudfunctions.net/api'

/**
 * Inicializa o SDK do Mercado Pago
 */
export function initMercadoPago() {
  if (window.MercadoPago) {
    const mp = new window.MercadoPago(MP_PUBLIC_KEY)
    return mp
  }
  console.warn('Mercado Pago SDK not loaded')
  return null
}

/**
 * Cria uma preferência de pagamento PIX
 */
export async function createPixPayment(amount, description, userId) {
  try {
    const txid = generateTxId()
    
    const response = await fetch(`${API_URL}/create-pix-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        amount,
        description,
        txid,
        userId,
        paymentMethod: 'pix'
      })
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || data.details || 'Failed to create PIX payment')
    }
    
    return {
      txid,
      qrCode: data.qrCode,
      qrCodeBase64: data.qrCodeBase64,
      pixCopyPaste: data.qrCodeData || data.pixCopyPaste,
      paymentId: data.paymentId,
      amount
    }
  } catch (error) {
    console.error('Error creating PIX payment:', error)
    throw error
  }
}

/**
 * Cria uma preferência de pagamento com Cartão
 */
export async function createCardPayment(cardData, amount, description, userId) {
  try {
    const txid = generateTxId()
    
    const response = await fetch(`${API_URL}/create-card-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        cardData,
        amount,
        description,
        txid,
        userId,
        paymentMethod: 'credit_card'
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to process card payment')
    }

    const data = await response.json()
    
    return {
      txid,
      paymentId: data.paymentId,
      status: data.status,
      amount
    }
  } catch (error) {
    console.error('Error processing card payment:', error)
    throw error
  }
}

/**
 * Tokeniza os dados do cartão usando o SDK do Mercado Pago
 */
export async function tokenizeCard(cardData) {
  const mp = initMercadoPago()
  
  if (!mp) {
    throw new Error('Mercado Pago SDK not initialized')
  }

  try {
    const cardToken = await mp.createCardToken({
      cardNumber: cardData.number,
      cardholderName: cardData.holderName,
      cardExpirationMonth: cardData.expirationMonth,
      cardExpirationYear: cardData.expirationYear,
      securityCode: cardData.cvv,
      identificationType: cardData.identificationType || 'CPF',
      identificationNumber: cardData.identificationNumber
    })

    return cardToken
  } catch (error) {
    console.error('Error tokenizing card:', error)
    throw new Error('Erro ao processar dados do cartão')
  }
}

/**
 * Verifica o status de um pagamento
 */
export async function checkPaymentStatus(txid) {
  try {
    const response = await fetch(`${API_URL}/check-payment/${txid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error('Failed to check payment status')
    }

    const data = await response.json()
    
    return {
      paid: data.status === 'PAID' || data.status === 'CONFIRMED' || data.status === 'approved',
      status: data.status,
      amount: data.amount,
      paidAt: data.paidAt,
      paymentMethod: data.paymentMethod
    }
  } catch (error) {
    console.error('Error checking payment status:', error)
    return { paid: false, status: 'PENDING' }
  }
}

/**
 * Obtém as parcelas disponíveis para um valor
 */
export async function getInstallments(amount, bin) {
  const mp = initMercadoPago()
  
  if (!mp) {
    return []
  }

  try {
    const installments = await mp.getInstallments({
      amount: String(amount),
      bin: bin,
      locale: 'pt-BR'
    })

    return installments[0]?.payer_costs || []
  } catch (error) {
    console.error('Error getting installments:', error)
    return []
  }
}

/**
 * Identifica a bandeira do cartão pelo BIN
 */
export async function getPaymentMethod(bin) {
  const mp = initMercadoPago()
  
  if (!mp) {
    return null
  }

  try {
    const paymentMethods = await mp.getPaymentMethods({
      bin: bin
    })

    return paymentMethods.results[0] || null
  } catch (error) {
    console.error('Error getting payment method:', error)
    return null
  }
}

/**
 * Gera um ID único para a transação
 */
export function generateTxId() {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 9).toUpperCase()
  return `GAME${timestamp}${random}`
}

/**
 * Formata número do cartão para exibição
 */
export function formatCardNumber(number) {
  return number.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim()
}

/**
 * Valida número do cartão (Algoritmo de Luhn)
 */
export function validateCardNumber(number) {
  const cleanNumber = number.replace(/\s/g, '')
  
  if (!/^\d{13,19}$/.test(cleanNumber)) {
    return false
  }

  let sum = 0
  let isEven = false

  for (let i = cleanNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cleanNumber.charAt(i), 10)

    if (isEven) {
      digit *= 2
      if (digit > 9) {
        digit -= 9
      }
    }

    sum += digit
    isEven = !isEven
  }

  return sum % 10 === 0
}

/**
 * Valida CVV
 */
export function validateCVV(cvv, cardNumber) {
  const cleanCVV = cvv.replace(/\s/g, '')
  const isAmex = cardNumber && (cardNumber.replace(/\s/g, '').startsWith('34') || cardNumber.replace(/\s/g, '').startsWith('37'))
  
  if (isAmex) {
    return /^\d{4}$/.test(cleanCVV)
  }
  
  return /^\d{3}$/.test(cleanCVV)
}

/**
 * Valida data de expiração
 */
export function validateExpiration(month, year) {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth() + 1

  const expYear = parseInt(year, 10)
  const expMonth = parseInt(month, 10)

  if (expYear < currentYear) {
    return false
  }

  if (expYear === currentYear && expMonth < currentMonth) {
    return false
  }

  return expMonth >= 1 && expMonth <= 12
}

/**
 * Formata valor monetário
 */
export function formatCurrency(amount) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(amount)
}
